import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AudioEditorLoaderPicker from "./AudioEditorLoaderPicker";
import AudioEditorLoaderItem from "./AudioEditorLoaderItem";
import SelectAudioModal from "../../../modals/SelectAudioModal";
import { ImportedAudioEditorTrackType, useAudioEditor } from "../../../../context/AudioEditorContext";
import { AudioAI, SoundLibraryAI } from "../../../../types/types";
import { ConvertAudioOptions } from "../utils";
import AudioEditorLoaderDropZone from "./AudioEditorLoaderDropZone";
import styles from "./AudioEditorLoader.module.css";

const AudioEditorLoaderSidebar = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const sidebarRef = useRef<HTMLDivElement>(null);
    const firstItemRef = useRef<HTMLDivElement | null>(null); // 🔸 Ref to first item

    const { importedAudioList, setConvertedAudioList, addToImportedList, selectedAudio, setSelectedAudio } =
        useAudioEditor();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    // Scroll to first item and center it when list changes
    useEffect(() => {
        if (importedAudioList.length > 0 && firstItemRef.current) {
            firstItemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [importedAudioList.length]);

    const handleAudioImport = (audio: AudioAI | SoundLibraryAI | null, options?: ConvertAudioOptions) => {
        if (audio) {
            // Add to imported list and get the converted audio
            const convertedAudio = addToImportedList(audio, options);

            // Automatically select the added audio if returned
            if (convertedAudio) {
                setSelectedAudio(convertedAudio);
            }
        }
    };

    const handleToggleSelect = (audio: ImportedAudioEditorTrackType) => {
        if (selectedAudio && selectedAudio.editorUID === audio.editorUID) {
            setSelectedAudio(null);
        } else {
            setSelectedAudio(audio);
        }
    };

    const handleDeleteAudio = (audioToDelete: ImportedAudioEditorTrackType) => {
        const updatedList = importedAudioList.filter((audio) => audio.editorUID !== audioToDelete.editorUID);
        setConvertedAudioList(updatedList);

        if (selectedAudio && selectedAudio.editorUID === audioToDelete.editorUID) {
            setSelectedAudio(null);
        }
    };

    return (
        <>
            <AudioEditorLoaderDropZone>
                <div className={styles.sidebar} ref={sidebarRef}>
                    <AudioEditorLoaderPicker helpText={t("importMedia")} onClick={() => setIsModalOpen(!isModalOpen)} />
                    {importedAudioList.length > 0 &&
                        importedAudioList.map((audio, index) => (
                            <AudioEditorLoaderItem
                                key={index}
                                ref={index === 0 ? firstItemRef : null} // 🔸 Attach to first item
                                title={audio.name}
                                isSelected={selectedAudio ? selectedAudio.editorUID === audio.editorUID : false}
                                onClick={() => handleToggleSelect(audio)}
                                onDelete={() => handleDeleteAudio(audio)}
                            />
                        ))}
                </div>
            </AudioEditorLoaderDropZone>
            <SelectAudioModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelectAudio={handleAudioImport}
            />
        </>
    );
};

export default AudioEditorLoaderSidebar;
